import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { Bell, CheckCircle, XCircle, Info, Warning, X } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function iconFor(type) {
  if (type === "success") return <CheckCircle size={14} weight="fill" className="text-emerald-400" />;
  if (type === "warning") return <Warning size={14} weight="fill" className="text-amber-400" />;
  if (type === "error") return <XCircle size={14} weight="fill" className="text-rose-400" />;
  return <Info size={14} weight="fill" className="text-sky-400" />;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${AFF_API}/affiliate/notifications`, { headers: getAffHeaders() });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch (err) {
      // Background poll: log at debug level and stay silent for the user.
      // A dropped fetch will retry on the next 60s interval automatically.
      if (err?.response?.status && err.response.status !== 401) {
        console.debug("[NotificationBell] load failed:", err.response?.status, err.message);
      }
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const markOne = async (id) => {
    try {
      await axios.post(`${AFF_API}/affiliate/notifications/${id}/read`, {}, { headers: getAffHeaders() });
      setItems((prev) => prev.map((n) => (n.notification_id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (err) {
      // Non-blocking: user can retry by clicking again. Reload from server
      // so local state doesn't drift from the DB.
      console.debug("[NotificationBell] markOne failed:", err?.response?.status);
      load();
    }
  };

  const markAll = async () => {
    try {
      await axios.post(`${AFF_API}/affiliate/notifications/read-all`, {}, { headers: getAffHeaders() });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (err) {
      console.debug("[NotificationBell] markAll failed:", err?.response?.status);
      load();
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open) load(); }}
        data-testid="aff-notif-bell"
        className="relative p-2 rounded-md text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} weight={unread > 0 ? "duotone" : "regular"} />
        {unread > 0 && (
          <span
            data-testid="aff-notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-[9px] font-bold text-white flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-zinc-800/50 bg-zinc-900 shadow-2xl z-50 overflow-hidden"
          data-testid="aff-notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/80">
            <div>
              <p className="text-sm font-bold text-white">Notifications</p>
              <p className="text-[10px] text-zinc-500">{unread} unread</p>
            </div>
            {items.length > 0 && unread > 0 && (
              <button
                onClick={markAll}
                data-testid="aff-notif-mark-all"
                className="text-[11px] text-sky-400 hover:text-sky-300 font-semibold"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={22} className="text-zinc-700 mx-auto mb-2" />
                <p className="text-xs text-zinc-500">No notifications yet</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.notification_id}
                  onClick={() => !n.read && markOne(n.notification_id)}
                  data-testid={`aff-notif-item-${n.notification_id}`}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors border-b border-zinc-800/30 ${
                    !n.read ? "bg-sky-500/[0.03]" : ""
                  }`}
                >
                  <div className="mt-0.5">{iconFor(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-zinc-100 truncate">{n.title}</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-zinc-600 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-sky-400 mt-1 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>

          <button
            onClick={() => setOpen(false)}
            className="w-full py-2 text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300 border-t border-zinc-800/80 flex items-center justify-center gap-1.5 transition-colors"
          >
            <X size={10} /> Close
          </button>
        </div>
      )}
    </div>
  );
}
